import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image } from 'antd';
import InfoText from '@/components/atoms/InfoText';
import StarRating from '@/components/atoms/StarRating';
import ExpandableText from '@/components/atoms/ExpandableText';
import { MagicStar } from 'iconsax-react';

interface FeedbackOverallReviewProps {
  totalReviewed: number;
  totalProducts: number;
  shipmentRating: number;
  shipmentReason?: string;
  shipmentNotes?: string;
  attachments?: string[];
  'data-testid'?: string;
}

export const FeedbackOverallReview: React.FC<FeedbackOverallReviewProps> = ({
  totalReviewed,
  totalProducts,
  shipmentRating,
  shipmentReason,
  shipmentNotes,
  attachments,
  'data-testid': dataTestId = 'store-overall-reviews',
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="p-4 border rounded-xl bg-light-surface-primary dark:bg-dark-surface-primary border-light-input-border dark:border-dark-input-border space-y-4"
      data-testid={dataTestId}
    >
      <div className="flex items-center gap-2" data-testid={`${dataTestId}-header`}>
        <MagicStar
          size={20}
          variant="Bold"
          className="text-light-primary dark:text-dark-primary"
        />
        <InfoText
          text={t('logisticsFeedback.overallReviewTitle')}
          className="!font-bold text-lg !text-light-primary dark:!text-dark-primary"
          data-testid={`${dataTestId}-title`}
        />
      </div>

      <div className="space-y-4" data-testid={`${dataTestId}-content`}>
        {/* Total products reviewed */}
        {totalProducts > 0 && (
          <div
            className="flex items-center justify-between"
            data-testid={`${dataTestId}-products-reviewed-row`}
          >
            <InfoText
              text={t('logisticsFeedback.totalMilestonesReviewed')}
              className="!text-base !font-normal !leading-none !text-light-primary dark:!text-dark-primary"
              data-testid={`${dataTestId}-products-reviewed-label`}
            />
            <InfoText
              text={`${totalReviewed}/${totalProducts}`}
              className="!text-base !font-bold !leading-none !text-light-primary dark:!text-dark-primary"
              data-testid={`${dataTestId}-products-reviewed-value`}
            />
          </div>
        )}

        {/* Shipment review rating */}
        <div
          className="flex items-center justify-between"
          data-testid={`${dataTestId}-shipment-rating-row`}
        >
          <InfoText
            text={t('logisticsFeedback.shipmentReview')}
            className="!text-sm !font-bold !leading-none !tracking-normal !text-light-primary dark:!text-dark-primary"
            data-testid={`${dataTestId}-shipment-rating-label`}
          />
          <div className="flex items-center gap-1.5" data-testid={`${dataTestId}-stars-container`}>
            <InfoText
              text={
                shipmentRating > 0
                  ? `(${shipmentRating.toFixed(1)})`
                  : '(0)'
              }
              className="!text-sm !font-bold !leading-none !text-light-primary dark:!text-dark-primary"
              data-testid={`${dataTestId}-shipment-rating-value`}
            />
            <StarRating
              value={shipmentRating}
              data-testid={`${dataTestId}-shipment-stars`}
            />
          </div>
        </div>

        {/* Shipment review reason */}
        {shipmentReason && (
          <div
            className="flex items-center justify-between"
            data-testid={`${dataTestId}-shipment-reason-row`}
          >
            <InfoText
              text={t('logisticsFeedback.shipmentReason')}
              className="!text-sm !font-normal !leading-none !text-light-text-secondary dark:!text-dark-text-secondary"
              data-testid={`${dataTestId}-shipment-reason-label`}
            />
            <InfoText
              text={shipmentReason}
              className="!text-sm !font-semibold !leading-none !text-light-primary dark:!text-dark-primary"
              data-testid={`${dataTestId}-shipment-reason-value`}
            />
          </div>
        )}

        {/* Shipment review notes */}
        {shipmentNotes && (
          <div
            className="space-y-1 pt-1 border-t border-light-input-border dark:border-dark-input-border"
            data-testid={`${dataTestId}-shipment-notes-wrapper`}
          >
            <InfoText
              text={t('logisticsFeedback.shipmentNotes')}
              className="!text-sm !font-bold !text-light-primary dark:!text-dark-primary"
              data-testid={`${dataTestId}-shipment-notes-label`}
            />
            <ExpandableText
              text={shipmentNotes}
              limit={140}
              className="!text-sm !font-normal !leading-relaxed !text-light-text-secondary dark:!text-dark-text-secondary"
              data-testid={`${dataTestId}-shipment-notes-text`}
            />
          </div>
        )}

        {/* Attachments */}
        {attachments && attachments.length > 0 && (
          <div
            className="space-y-1.5 pt-1 border-t border-light-input-border dark:border-dark-input-border"
            data-testid={`${dataTestId}-attachments-wrapper`}
          >
            <InfoText
              text={t('logisticsFeedback.attachments')}
              className="!font-bold !text-sm !text-light-primary dark:!text-dark-primary"
              data-testid={`${dataTestId}-attachments-title`}
            />
            <div
              className="flex flex-row flex-wrap gap-3"
              data-testid={`${dataTestId}-attachments-list`}
            >
              {attachments.map((imgUrl, imgIdx) => (
                <Image
                  key={`overall-attachment-${imgIdx}`}
                  src={imgUrl}
                  width={64}
                  height={64}
                  alt={`overall-attachment-${imgIdx}`}
                  className="rounded-[12px] object-cover border border-light-input-border dark:border-dark-input-border"
                  data-testid={`${dataTestId}-attachment-image-${imgIdx}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
